import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Sparkles,
  FileSearch,
  Calculator,
  BookOpen,
  MessageCircle,
  Upload,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Send,
  Home,
  Hammer,
  Euro,
  Wrench,
  ListOrdered,
  Shield,
  Clock,
  Camera,
  HardHat,
  FileText,
  EyeOff
} from 'lucide-react';
import { Button } from './ui/button';
import LeonAnimated from './LeonAnimated';

// =====================================================
// CONFIGURATIONS DES TUTORIELS PAR MODULE
// =====================================================

export const TUTORIAL_CONFIGS = {
  // Tutoriel Diagnostic Photo (V1)
  diagnostic: {
    id: 'diagnostic',
    storageKey: 'phenix_tutorial_diagnostic',
    title: 'Diagnostic Photo',
    welcomeMessage: 'Je vais vous montrer comment obtenir une analyse détaillée à partir d\'une simple photo.',
    actionButton: {
      label: 'Analyser une photo',
      testId: 'diagnostic-upload-btn'
    },
    steps: [
      {
        id: 'upload-photo',
        title: 'Envoyez votre photo',
        description: 'Prenez en photo la zone que vous souhaitez analyser (mur, sol, plafond, installation...).',
        target: null,
        icon: Camera,
        leonTip: 'Une photo nette et bien éclairée me permet de mieux identifier les problèmes potentiels !'
      },
      {
        id: 'add-context',
        title: 'Ajoutez du contexte (optionnel)',
        description: 'Décrivez brièvement ce que vous voyez ou ce qui vous inquiète. Cela m\'aide à affiner mon analyse.',
        target: null,
        icon: FileText,
        leonTip: 'Plus vous me donnez de contexte, plus mon diagnostic sera précis et personnalisé.'
      },
      {
        id: 'get-analysis',
        title: 'Recevez votre diagnostic',
        description: 'J\'analyse l\'image et vous fournis : identification du problème, points d\'attention et recommandations.',
        target: null,
        icon: BookOpen,
        leonTip: 'Mon analyse est pédagogique : je vous explique ce que je vois et ce qu\'il faut surveiller.'
      }
    ]
  },

  // Tutoriel Audit de Devis
  analyze: {
    id: 'analyze',
    storageKey: 'phenix_tutorial_analyze',
    title: 'Audit de Devis',
    welcomeMessage: 'Je vais vous montrer comment analyser un devis et détecter les anomalies en quelques clics.',
    actionButton: {
      label: 'Analyser mon devis',
      testId: 'analyze-quote-btn'
    },
    steps: [
      {
        id: 'upload-quote',
        title: 'Télécharger votre devis',
        description: 'Glissez-déposez votre devis (PDF ou image) dans la zone prévue, ou cliquez pour sélectionner un fichier.',
        target: '[data-testid="upload-zone"]',
        icon: Upload,
        leonTip: 'J\'accepte les PDF et les images. Vous pouvez même prendre une photo de votre devis papier !'
      },
      {
        id: 'ai-analysis',
        title: 'Analyse automatique par IA',
        description: 'Mon intelligence artificielle analyse chaque ligne du devis : prix, quantités, TVA, mentions obligatoires...',
        target: null,
        icon: Eye,
        leonTip: 'Je compare les prix avec mes bases de données actualisées pour détecter les surfacturations.'
      },
      {
        id: 'alerts-review',
        title: 'Alertes et points d\'attention',
        description: 'Je vous signale les anomalies détectées : prix incohérents, mentions manquantes, postes suspects.',
        target: null,
        icon: AlertTriangle,
        leonTip: 'Chaque alerte est accompagnée d\'une explication pour vous aider à comprendre le problème.'
      },
      {
        id: 'validation-admin',
        title: 'Vérification administrative',
        description: 'Je vérifie que le devis respecte les obligations légales : SIRET, assurance, CGV, délais...',
        target: null,
        icon: Shield,
        leonTip: 'Un devis conforme vous protège en cas de litige. Ne négligez pas ces vérifications !'
      },
      {
        id: 'action-decision',
        title: 'Prise de décision',
        description: 'Grâce à mon analyse, vous pouvez négocier les points problématiques ou demander un nouveau devis.',
        target: null,
        icon: CheckCircle2,
        leonTip: 'Vous pouvez télécharger un rapport PDF de l\'analyse pour le garder ou le partager.'
      }
    ]
  },

  // Tutoriel Budget Travaux
  budget: {
    id: 'budget',
    storageKey: 'phenix_tutorial_budget',
    title: 'Budget Travaux',
    welcomeMessage: 'Je vais vous aider à estimer le budget de vos travaux de rénovation en quelques étapes.',
    actionButton: {
      label: 'Estimer mes travaux',
      testId: 'start-estimation-btn'
    },
    steps: [
      {
        id: 'property-type',
        title: 'Décrire votre bien',
        description: 'Commencez par indiquer le type de bien (appartement, maison), sa surface et son année de construction.',
        target: '[data-testid="property-step"]',
        icon: Home,
        leonTip: 'Ces informations me permettent d\'ajuster les prix en fonction des spécificités de votre bien.'
      },
      {
        id: 'renovation-level',
        title: 'Niveau de rénovation',
        description: 'Choisissez entre rafraîchissement, rénovation partielle ou rénovation complète selon vos besoins.',
        target: null,
        icon: Hammer,
        leonTip: 'Le niveau de rénovation influence fortement le budget. Un rafraîchissement coûte 3 à 5 fois moins qu\'une rénovation complète.'
      },
      {
        id: 'work-details',
        title: 'Détails des travaux',
        description: 'Sélectionnez les postes de travaux concernés : électricité, plomberie, sols, peinture, aménagement...',
        target: null,
        icon: Wrench,
        leonTip: 'Cochez tous les postes même si vous n\'êtes pas sûr. Mieux vaut prévoir large que d\'avoir des surprises !'
      },
      {
        id: 'complexity-factors',
        title: 'Facteurs de complexité',
        description: 'Indiquez les contraintes : accès difficile, étage élevé, travaux en site occupé...',
        target: null,
        icon: AlertTriangle,
        leonTip: 'Ces facteurs peuvent augmenter le coût de 10 à 25%. Soyez honnête pour avoir une estimation réaliste.'
      },
      {
        id: 'budget-result',
        title: 'Résultat et fourchette',
        description: 'Obtenez une estimation avec une fourchette basse et haute, ainsi qu\'un détail par poste.',
        target: null,
        icon: Euro,
        leonTip: 'L\'estimation est indicative. Demandez toujours plusieurs devis pour comparer les prix réels !'
      }
    ]
  },

  // Tutoriel Recette Travaux (DIY)
  recipes: {
    id: 'recipes',
    storageKey: 'phenix_tutorial_recipes',
    title: 'Recette Travaux',
    welcomeMessage: 'Je vais vous expliquer comment obtenir un guide étape par étape pour vos travaux DIY.',
    actionButton: {
      label: 'Commencer mes travaux',
      testId: 'generate-recipe-btn'
    },
    steps: [
      {
        id: 'describe-project',
        title: 'Décrire votre projet',
        description: 'Décrivez les travaux que vous souhaitez réaliser : type, surface, matériaux souhaités...',
        target: '[data-testid="project-textarea"]',
        icon: FileText,
        leonTip: 'Plus votre description est précise, plus la recette sera adaptée à votre situation !'
      },
      {
        id: 'ai-generation',
        title: 'Génération de la recette',
        description: 'Mon IA génère un guide complet avec les étapes détaillées, le temps estimé et la difficulté.',
        target: null,
        icon: Sparkles,
        leonTip: 'Je prends en compte les normes DTU et les bonnes pratiques du métier pour vous guider.'
      },
      {
        id: 'steps-list',
        title: 'Étapes détaillées',
        description: 'Chaque étape est expliquée clairement avec les gestes techniques et les points d\'attention.',
        target: null,
        icon: ListOrdered,
        leonTip: 'Vous pouvez cocher les étapes au fur et à mesure de votre avancement.'
      },
      {
        id: 'materials-list',
        title: 'Liste du matériel',
        description: 'Obtenez la liste complète des outils et matériaux nécessaires avec les quantités estimées.',
        target: null,
        icon: Wrench,
        leonTip: 'Préparez tout le matériel avant de commencer pour éviter les allers-retours en magasin !'
      },
      {
        id: 'print-recipe',
        title: 'Imprimer ou télécharger',
        description: 'Téléchargez votre recette en PDF pour l\'avoir sous la main pendant vos travaux.',
        target: null,
        icon: FileText,
        leonTip: 'Le PDF est parfait pour avoir les instructions à côté de vous sur le chantier.'
      }
    ]
  },

  // Tutoriel Assistant IA
  chat: {
    id: 'chat',
    storageKey: 'phenix_tutorial_chat',
    title: 'Assistant Léon',
    welcomeMessage: 'Je suis votre expert travaux personnel. Découvrez comment je peux vous aider.',
    actionButton: {
      label: 'Poser une question',
      testId: 'chat-input'
    },
    steps: [
      {
        id: 'ask-question',
        title: 'Posez votre question',
        description: 'Écrivez votre question sur les travaux, la rénovation, les matériaux, les normes...',
        target: '[data-testid="chat-input"]',
        icon: MessageCircle,
        leonTip: 'Soyez précis dans vos questions pour obtenir des réponses adaptées à votre situation.'
      },
      {
        id: 'photo-analysis',
        title: 'Envoyez une photo',
        description: 'Vous pouvez m\'envoyer une photo de votre chantier et je l\'analyserai pour vous conseiller.',
        target: '[data-testid="upload-image-btn"]',
        icon: Camera,
        leonTip: 'Une photo vaut mille mots ! Je peux identifier des problèmes, des matériaux ou suggérer des solutions.'
      },
      {
        id: 'expert-response',
        title: 'Réponse experte',
        description: 'Je vous réponds avec des conseils basés sur les normes DTU, les prix du marché et les bonnes pratiques.',
        target: null,
        icon: Sparkles,
        leonTip: 'Mes réponses sont personnalisées. N\'hésitez pas à me poser des questions de suivi !'
      },
      {
        id: 'suggestions',
        title: 'Suggestions de questions',
        description: 'Des suggestions de questions apparaissent pour vous aider à approfondir vos recherches.',
        target: null,
        icon: Sparkles,
        leonTip: 'Cliquez sur une suggestion pour l\'envoyer directement. C\'est pratique et rapide !'
      },
      {
        id: 'history',
        title: 'Historique des conversations',
        description: 'Votre historique est sauvegardé. Vous pouvez le relire ou l\'effacer à tout moment.',
        target: null,
        icon: Clock,
        leonTip: 'Utilisez l\'historique pour retrouver un conseil donné précédemment sans me reposer la question.'
      }
    ]
  }
};

// =====================================================
// COMPOSANTS RÉUTILISABLES
// =====================================================

// Carte de bienvenue générique
export function TutorialWelcomeCard({ config, onStart, onDismiss, onDisableTutorials }) {
  const [disabling, setDisabling] = useState(false);
  
  const handleDisableTutorials = async () => {
    setDisabling(true);
    if (onDisableTutorials) {
      await onDisableTutorials();
    }
    setDisabling(false);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="glass-card p-6 mb-8 border-gold-500/30 bg-gradient-to-r from-gold-500/10 via-transparent to-gold-500/10"
      data-testid={`tutorial-welcome-${config.id}`}
    >
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <LeonAnimated className="w-24 h-24 flex-shrink-0" showTip={false} />
        <div className="flex-1 text-center sm:text-left">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center justify-center sm:justify-start gap-2">
            <Sparkles className="w-5 h-5 text-gold-500" />
            Bienvenue !
          </h2>
          <p className="text-zinc-300 mb-4">
            {config.welcomeMessage}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            <Button
              onClick={onStart}
              className="bg-gold-500 hover:bg-gold-400 text-black font-bold"
              data-testid={`start-tutorial-${config.id}-btn`}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Commencer la visite
            </Button>
            <Button
              onClick={onDismiss}
              variant="ghost"
              className="text-zinc-400 hover:text-white"
              data-testid={`dismiss-tutorial-${config.id}-btn`}
            >
              Plus tard
            </Button>
          </div>
          {/* Option pour désactiver les tutoriels */}
          <button
            onClick={handleDisableTutorials}
            disabled={disabling}
            className="text-xs text-zinc-500 hover:text-zinc-400 transition-colors flex items-center gap-1.5 mx-auto sm:mx-0"
            data-testid="disable-tutorials-btn"
          >
            <EyeOff className="w-3 h-3" />
            {disabling ? 'Désactivation...' : 'Ne plus afficher les tutoriels'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Composant principal du tutoriel générique
export function LeonTutorial({ isOpen, onClose, onComplete, config }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  
  const steps = config?.steps || [];
  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Trouver et surligner l'élément cible
  const updateHighlight = useCallback(() => {
    const currentStepData = steps[currentStep];
    if (currentStepData?.target) {
      const element = document.querySelector(currentStepData.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setHighlightRect(null);
      }
    } else {
      setHighlightRect(null);
    }
  }, [steps, currentStep]);

  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Update highlight when step changes
  useEffect(() => {
    if (isOpen) {
      updateHighlight();
      window.addEventListener('resize', updateHighlight);
      window.addEventListener('scroll', updateHighlight);
      return () => {
        window.removeEventListener('resize', updateHighlight);
        window.removeEventListener('scroll', updateHighlight);
      };
    }
  }, [isOpen, currentStep, updateHighlight]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
    onClose();
  };

  const StepIcon = step?.icon;
  const SecondaryIcon = step?.secondaryIcon;

  return (
    <AnimatePresence mode="wait">
      {isOpen && config && (
        <div key="tutorial-overlay" className="fixed inset-0 z-[100]">
          {/* Overlay sombre avec découpe pour l'élément surligné */}
          <div className="absolute inset-0 bg-black/80" onClick={handleSkip}>
            {highlightRect && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute border-2 border-gold-500 rounded-xl pointer-events-none"
                style={{
                  top: highlightRect.top,
                  left: highlightRect.left,
                  width: highlightRect.width,
                  height: highlightRect.height,
                  boxShadow: '0 0 20px rgba(212, 175, 55, 0.5), 0 0 0 9999px rgba(0,0,0,0.8)'
                }}
              />
            )}
          </div>

          {/* Modal du tutoriel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 w-full max-w-lg mx-auto px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
              {/* Header avec progression */}
              <div className="px-6 pt-5 pb-3 border-b border-zinc-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <LeonAnimated className="w-10 h-10" showTip={false} />
                    <span className="text-gold-500 font-medium">Tutoriel : {config.title}</span>
                  </div>
                  <button
                    onClick={handleSkip}
                    className="w-8 h-8 rounded-full hover:bg-zinc-800 flex items-center justify-center transition-colors"
                    data-testid="close-tutorial-btn"
                  >
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
                
                {/* Barre de progression */}
                <div className="flex gap-1.5">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        index <= currentStep ? 'bg-gold-500' : 'bg-zinc-700'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Étape {currentStep + 1} sur {steps.length}
                </p>
              </div>

              {/* Contenu de l'étape */}
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                    {StepIcon && <StepIcon className="w-6 h-6 text-gold-500" />}
                  </div>
                  {SecondaryIcon && (
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0 -ml-6">
                      <SecondaryIcon className="w-6 h-6 text-emerald-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{step?.title}</h3>
                    <p className="text-zinc-400 text-sm">{step?.description}</p>
                  </div>
                </div>

                {/* Conseil de Léon */}
                <div className="p-4 bg-gold-500/10 border border-gold-500/20 rounded-xl">
                  <p className="text-gold-400 text-xs font-medium mb-1">Conseil de Léon</p>
                  <p className="text-zinc-300 text-sm">{step?.leonTip}</p>
                </div>
              </div>

              {/* Footer avec navigation */}
              <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between">
                <Button
                  onClick={handlePrev}
                  disabled={isFirstStep}
                  variant="ghost"
                  className={`${isFirstStep ? 'invisible' : 'text-zinc-400'}`}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Précédent
                </Button>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSkip}
                    className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Passer
                  </button>
                  <Button
                    onClick={handleNext}
                    className="bg-gold-500 hover:bg-gold-400 text-black font-bold"
                    data-testid="next-tutorial-btn"
                  >
                    {isLastStep ? (
                      <>
                        {config.actionButton?.label || 'Terminer'}
                        <Sparkles className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Suivant
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Hook personnalisé pour gérer l'état du tutoriel
export function useTutorial(moduleId = 'projects') {
  const { tutorialsEnabled, setTutorialsEnabled } = useAuth();
  const config = TUTORIAL_CONFIGS[moduleId];
  const STORAGE_KEY = config?.storageKey || `phenix_tutorial_${moduleId}`;
  
  const [hasSeenTutorial, setHasSeenTutorial] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Ne montrer la bienvenue que si :
    // 1. Les tutoriels sont activés globalement
    // 2. L'utilisateur n'a pas encore vu ce tutoriel
    if (tutorialsEnabled && !hasSeenTutorial) {
      setShowWelcome(true);
    } else {
      setShowWelcome(false);
    }
  }, [hasSeenTutorial, tutorialsEnabled]);

  const startTutorial = () => {
    setShowWelcome(false);
    setShowTutorial(true);
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
  };

  const completeTutorial = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setHasSeenTutorial(true);
    setShowTutorial(false);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
  };

  const restartTutorial = () => {
    if (tutorialsEnabled) {
      setShowTutorial(true);
    } else {
      toast.info('Activez les tutoriels dans les paramètres pour revoir cette visite guidée.');
    }
  };

  const resetTutorial = () => {
    localStorage.removeItem(STORAGE_KEY);
    setHasSeenTutorial(false);
    if (tutorialsEnabled) {
      setShowWelcome(true);
    }
  };

  // Fonction pour désactiver tous les tutoriels
  const disableAllTutorials = async () => {
    const success = await setTutorialsEnabled(false);
    if (success) {
      setShowWelcome(false);
      setShowTutorial(false);
      toast.success('Tutoriels masqués. Vous pouvez les réactiver dans les paramètres.', {
        duration: 4000,
        icon: '👋'
      });
    }
    return success;
  };

  return {
    config,
    hasSeenTutorial,
    showWelcome: tutorialsEnabled && showWelcome,
    showTutorial,
    tutorialsEnabled,
    startTutorial,
    dismissWelcome,
    completeTutorial,
    closeTutorial,
    restartTutorial,
    resetTutorial,
    disableAllTutorials
  };
}

export default LeonTutorial;
